/**
 * Start another company from inside the app.
 *
 * One Railit account, many companies: the identity is global and each
 * organization is a separate tenant with its own data, its own team and its own
 * subscription billed to whoever owns it. Creating one here makes you its Owner
 * and drops you straight into it, because the only reason to create a workspace
 * is to start using it.
 */

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useI18n } from '@/contexts/I18nContext';
import { useAuth } from '@/contexts/AuthContext';
import { pushToast } from '@/components/ui/Toast';
import { Button } from '@/components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/shadcn/dialog';
import { Input } from '@/components/shadcn/input';
import { Label } from '@/components/shadcn/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/shadcn/select';

interface NewOrgDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface CreateResponse {
  organization: { id: string; slug: string; name: string };
}

/** Countries we quote a currency for. Anything else defaults to USD. */
const COUNTRIES: { code: string; currency: string; labelKey: string }[] = [
  { code: 'BH', currency: 'BHD', labelKey: 'country_bh' },
  { code: 'SA', currency: 'SAR', labelKey: 'country_sa' },
  { code: 'AE', currency: 'AED', labelKey: 'country_ae' },
  { code: 'KW', currency: 'KWD', labelKey: 'country_kw' },
  { code: 'QA', currency: 'QAR', labelKey: 'country_qa' },
  { code: 'OM', currency: 'OMR', labelKey: 'country_om' },
];

export function NewOrgDialog({ open, onOpenChange }: NewOrgDialogProps) {
  const { t, lang } = useI18n();
  const { switchOrg, refresh } = useAuth();
  const [name, setName] = useState('');
  const [country, setCountry] = useState('BH');

  const currency = COUNTRIES.find((c) => c.code === country)?.currency ?? 'USD';

  const create = useMutation<CreateResponse, ApiError, void>({
    mutationFn: () =>
      api<CreateResponse>('/org/new', {
        method: 'POST',
        body: JSON.stringify({
          name: name.trim(),
          country,
          base_currency: currency,
          locale: lang,
        }),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: async (res) => {
      // Refresh the membership list first, or switchOrg would move into an
      // organization the client does not know about yet.
      await refresh();
      onOpenChange(false);
      setName('');
      pushToast({ type: 'success', message: t('org_created').replace('{name}', res.organization.name) });
      await switchOrg(res.organization.id);
    },
    onError: (e) => pushToast({ type: 'error', message: e.message }),
  });

  const canSubmit = name.trim().length >= 2 && !create.isPending;

  return (
    <Dialog open={open} onOpenChange={(v) => !create.isPending && onOpenChange(v)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('new_company')}</DialogTitle>
          <DialogDescription>{t('new_company_hint')}</DialogDescription>
        </DialogHeader>

        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) create.mutate();
          }}
        >
          <div className="grid gap-2">
            <Label htmlFor="new-org-name">{t('company_name')}</Label>
            <Input
              id="new-org-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Acme Trading W.L.L."
              autoFocus
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="new-org-country">{t('country')}</Label>
            <Select value={country} onValueChange={setCountry}>
              <SelectTrigger id="new-org-country" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COUNTRIES.map((c) => (
                  <SelectItem key={c.code} value={c.code}>
                    {t(c.labelKey)} · {c.currency}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">{t('new_company_trial')}</p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={create.isPending}
            >
              {t('cancel')}
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {create.isPending && <Loader2 className="size-4 animate-spin" />}
              {t('create_company')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
